/**
 * VersionDropdown
 * Visar aktuell produktversion och historik från /api/version/releases.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { productReleaseApi, type ProductRelease } from '@/services/version/productReleaseApi';

const VersionDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [releases, setReleases] = useState<ProductRelease[]>([]);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const swedish = (i18n.language || 'sv').startsWith('sv');

  useEffect(() => {
    let active = true;
    productReleaseApi.list()
      .then((data) => {
        if (!active) return;
        setCurrentVersion(data.currentVersion);
        setReleases(data.releases);
        if (data.currentVersion) setExpandedVersions(new Set([data.currentVersion]));
      })
      .catch(() => {
        if (active) setCurrentVersion(null);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleVersion = (version: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
  };

  const label = currentVersion ? `v${currentVersion}` : 'v…';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 px-2 text-xs cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center gap-1 text-text-muted hover:text-text-default"
        aria-label={t('version.title') || 'Version'}
        title={`${t('version.title') || 'Version'}: ${label}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <span className="font-mono font-medium">{label}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-bgContainer rounded-lg shadow-lg border border-border z-50 overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-text-default">DQFleet</h3>
                <p className="text-xs text-text-muted">{t('version.fleetManagement') || 'Fleet Management System'}</p>
              </div>
              <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-mono rounded">{label}</span>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <div className="px-4 py-2 bg-bgLight/30 border-b border-border">
              <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide">
                {t('version.releaseHistory') || 'Versionshistorik'}
              </h4>
            </div>
            {releases.length === 0 ? (
              <p className="px-4 py-3 text-sm text-text-muted">{t('version.noReleases') || 'Ingen versionshistorik'}</p>
            ) : (
              releases.map((release) => {
                const expanded = expandedVersions.has(release.version);
                const summary = swedish ? release.summarySv : release.summaryEn;
                return (
                  <div key={release.version} className="border-b border-border last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleVersion(release.version)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-bgLight/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold text-text-default">v{release.version}</span>
                        {release.status === 'current' && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded">
                            {t('version.current') || 'Aktuell'}
                          </span>
                        )}
                        {release.status === 'released' && (
                          <span className="px-2 py-0.5 bg-bgLight text-text-muted text-xs font-medium rounded">
                            {t('version.released') || 'Släppt'}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-text-muted">{release.releasedOn}</span>
                    </button>
                    {expanded && (
                      <div className="px-4 pb-3 pt-1">
                        <p className="text-sm text-text-default pl-2 mb-2">{release.title}</p>
                        <p className="text-sm text-text-muted pl-2 mb-2">{summary}</p>
                        <div className="space-y-2 pl-2 border-l-2 border-primary/30">
                          {release.notes.map((note) => (
                            <div key={note.textSv} className="flex items-start gap-2 text-sm pl-2">
                              <span className="text-green-500">✓</span>
                              <span className="text-text-muted">{swedish ? note.textSv : note.textEn}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <div className="px-4 py-3 bg-bgLight/50 border-t border-border text-center text-xs text-text-muted">
            © 2026 Deviaq AB
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionDropdown;
